import { useEffect, useMemo, useState } from 'react'
import PageLayout from '../common/layout/PageLayout'
import {
  getNotificationEmailTesterTemplates,
  testNotificationEmailTemplate,
} from '../../api/adminNotificationsService'

function prettyJson(value) {
  return JSON.stringify(value, null, 2)
}

function AdminEmailTemplateTester() {
  const [templates, setTemplates] = useState([])
  const [selectedTemplateKey, setSelectedTemplateKey] = useState('')
  const [recipientEmail, setRecipientEmail] = useState('')
  const [payloadText, setPayloadText] = useState('{}')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState(null)

  useEffect(() => {
    let mounted = true

    async function loadTemplates() {
      try {
        setLoading(true)
        setError('')

        const response = await getNotificationEmailTesterTemplates()
        const options = response.templates || []

        if (!mounted) return

        setTemplates(options)

        if (options.length > 0) {
          const first = options[0]
          setSelectedTemplateKey(first.key)
          setPayloadText(prettyJson(first.sample_payload || {}))
        }
      } catch (err) {
        if (!mounted) return
        setError(err.message || 'Failed to load template metadata')
      } finally {
        if (mounted) setLoading(false)
      }
    }

    loadTemplates()

    return () => {
      mounted = false
    }
  }, [])

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.key === selectedTemplateKey),
    [templates, selectedTemplateKey],
  )

  const handleTemplateChange = (event) => {
    const nextKey = event.target.value
    setSelectedTemplateKey(nextKey)
    const next = templates.find((template) => template.key === nextKey)
    setPayloadText(prettyJson(next?.sample_payload || {}))
    setResult(null)
    setError('')
  }

  const handleRun = async ({ previewOnly }) => {
    try {
      setSubmitting(true)
      setError('')

      let parsedPayload = {}
      if (payloadText.trim().length > 0) {
        parsedPayload = JSON.parse(payloadText)
      }

      if (
        parsedPayload === null ||
        typeof parsedPayload !== 'object' ||
        Array.isArray(parsedPayload)
      ) {
        throw new Error(
          'Payload JSON must be an object (for example: {"league_id": 12})',
        )
      }

      const response = await testNotificationEmailTemplate({
        template_key: selectedTemplateKey,
        to: recipientEmail,
        payload: parsedPayload,
        preview_only: previewOnly,
      })

      setResult(response)
    } catch (err) {
      setResult(null)
      setError(err.message || 'Failed to run template test')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <PageLayout title="Admin Email Template Tester">
      <div className="container-fluid py-3 py-md-4">
        <div className="row mb-3">
          <div className="col-12">
            <h1 className="h3 mb-1">Admin Email Template Tester</h1>
            <p className="text-muted mb-0">
              Preview and send test emails using live template rendering and
              payload data.
            </p>
          </div>
        </div>

        {error && (
          <div className="row mb-3">
            <div className="col-12">
              <div
                className="alert alert-danger"
                role="alert"
                data-testid="email-tester-error"
              >
                {error}
              </div>
            </div>
          </div>
        )}

        <div className="row g-3">
          <div className="col-12 col-lg-5">
            <div className="card h-100">
              <div className="card-body">
                <h2 className="h5 mb-3">Test Inputs</h2>

                <div className="mb-3">
                  <label htmlFor="templateKey" className="form-label">
                    Template
                  </label>
                  <select
                    id="templateKey"
                    className="form-select"
                    value={selectedTemplateKey}
                    onChange={handleTemplateChange}
                    disabled={loading || submitting || templates.length === 0}
                    data-testid="email-tester-template-select"
                  >
                    {templates.map((template) => (
                      <option key={template.key} value={template.key}>
                        {template.key}
                      </option>
                    ))}
                  </select>
                  {selectedTemplate?.subject && (
                    <div className="form-text">
                      Subject: {selectedTemplate.subject}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label htmlFor="recipientEmail" className="form-label">
                    Recipient Email (required for send)
                  </label>
                  <input
                    id="recipientEmail"
                    type="email"
                    className="form-control"
                    value={recipientEmail}
                    onChange={(event) => setRecipientEmail(event.target.value)}
                    placeholder="qa+emails@yourcompany.com"
                    disabled={loading || submitting}
                    data-testid="email-tester-recipient-input"
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="payloadJson" className="form-label">
                    Payload JSON
                  </label>
                  <textarea
                    id="payloadJson"
                    className="form-control font-monospace"
                    rows={10}
                    value={payloadText}
                    onChange={(event) => setPayloadText(event.target.value)}
                    disabled={loading || submitting}
                    data-testid="email-tester-payload-input"
                  />
                </div>

                <div className="d-flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => handleRun({ previewOnly: true })}
                    disabled={loading || submitting || !selectedTemplateKey}
                    data-testid="email-tester-preview-button"
                  >
                    {submitting ? 'Working...' : 'Preview Template'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => handleRun({ previewOnly: false })}
                    disabled={loading || submitting || !selectedTemplateKey}
                    data-testid="email-tester-send-button"
                  >
                    {submitting ? 'Sending...' : 'Send Test Email'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-7">
            <div className="card h-100">
              <div className="card-body">
                <h2 className="h5 mb-3">Preview Result</h2>

                {!result && (
                  <div
                    className="text-muted"
                    data-testid="email-tester-empty-result"
                  >
                    Run a preview to render subject, links, text, and HTML
                    output.
                  </div>
                )}

                {result && (
                  <>
                    <div className="mb-2">
                      <strong>Subject:</strong> {result.subject}
                    </div>
                    <div className="mb-3">
                      <strong>Mode:</strong>{' '}
                      {result.preview_only
                        ? 'Preview only'
                        : 'Sent to recipient'}
                    </div>

                    <div className="mb-3">
                      <strong>Resolved Links</strong>
                      {result.links?.length ? (
                        <ul className="mt-2 mb-0">
                          {result.links.map((link) => (
                            <li key={link}>
                              <a href={link} target="_blank" rel="noreferrer">
                                {link}
                              </a>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="text-muted">
                          No links found in rendered output.
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label mb-1">
                        <strong>Text Preview</strong>
                      </label>
                      <pre
                        className="bg-light p-3 rounded border small mb-0"
                        style={{ whiteSpace: 'pre-wrap' }}
                      >
                        {result.text}
                      </pre>
                    </div>

                    <div>
                      <label className="form-label mb-1">
                        <strong>HTML Preview</strong>
                      </label>
                      <iframe
                        title="Rendered email HTML preview"
                        srcDoc={result.html}
                        className="w-100 border rounded"
                        style={{ minHeight: 360 }}
                        sandbox=""
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  )
}

export default AdminEmailTemplateTester
